import * as THREE from 'three';

/**
 * Character pawn mesh factories — three.js primitive assemblies only
 * (Cone / Cylinder / Sphere / Box / Torus; no external assets or textures).
 *
 * Every pawn:
 *  - is a THREE.Group whose origin sits on the tile surface (y = 0),
 *  - faces +z (player 0 looks toward its goal; BoardView rotates player 1),
 *  - stays around 0.9 tall and inside the 1.0 tile footprint,
 *  - keeps the player color (P0 blue / P1 red) as its dominant color and
 *    relies on silhouette + accents to tell characters apart,
 *  - gets castShadow enabled on all meshes.
 */

export const PLAYER_COLORS = [0x4da3ff, 0xff6b6b];

function shaded(hex, dl) {
  const c = new THREE.Color(hex);
  c.offsetHSL(0, 0, dl);
  return c;
}

function mat(color, opts = {}) {
  return new THREE.MeshStandardMaterial({
    color,
    roughness: 0.45,
    metalness: 0.12,
    ...opts,
  });
}

/* --- なし: クラシックなポーン（円柱 + 球頭） --- */
function buildClassic(color) {
  const g = new THREE.Group();
  const m = mat(color, { roughness: 0.35, metalness: 0.15 });
  const body = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.32, 0.6, 20), m);
  body.position.y = 0.3;
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.22, 20, 16), m);
  head.position.y = 0.72;
  g.add(body, head);
  return g;
}

/* --- 忍者: 低い構えの濃色シルエット + プレイヤー色の鉢巻/帯 + 背中の刀 --- */
function buildNinja(color) {
  const g = new THREE.Group();
  const dark = mat(0x262a33, { roughness: 0.6 });
  const accent = mat(color, { roughness: 0.4 });

  const body = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.3, 0.42, 18), dark);
  body.position.y = 0.21;

  const belt = new THREE.Mesh(new THREE.TorusGeometry(0.23, 0.035, 10, 24), accent);
  belt.rotation.x = Math.PI / 2;
  belt.position.y = 0.12;

  const head = new THREE.Mesh(new THREE.SphereGeometry(0.16, 18, 14), dark);
  head.position.y = 0.53;

  const band = new THREE.Mesh(new THREE.TorusGeometry(0.15, 0.035, 10, 24), accent);
  band.rotation.x = Math.PI / 2;
  band.position.y = 0.57;

  // 鉢巻の結び目のたれ（背中側）
  const tail = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.13, 0.02), accent);
  tail.position.set(0.04, 0.49, -0.15);
  tail.rotation.x = 0.35;

  // 背中の刀（刀身=薄いBox、柄=プレイヤー色）
  const sword = new THREE.Group();
  const blade = new THREE.Mesh(
    new THREE.BoxGeometry(0.035, 0.52, 0.015),
    mat(0xd6dde8, { metalness: 0.7, roughness: 0.25 }),
  );
  const grip = new THREE.Mesh(new THREE.BoxGeometry(0.045, 0.14, 0.025), accent);
  grip.position.y = 0.31;
  sword.add(blade, grip);
  sword.position.set(0, 0.34, -0.18);
  sword.rotation.z = 0.65;

  g.add(body, belt, head, band, tail, sword);
  return g;
}

/* --- 魔術師: プレイヤー色ローブ + とんがり帽子 + 発光オーブ --- */
function buildMage(color) {
  const g = new THREE.Group();
  const robe = new THREE.Mesh(new THREE.ConeGeometry(0.3, 0.58, 20), mat(color));
  robe.position.y = 0.29;

  const head = new THREE.Mesh(
    new THREE.SphereGeometry(0.13, 18, 14),
    mat(shaded(color, 0.18)),
  );
  head.position.y = 0.62;

  const hatMat = mat(shaded(color, -0.12));
  const brim = new THREE.Mesh(new THREE.CylinderGeometry(0.24, 0.24, 0.025, 20), hatMat);
  brim.position.y = 0.68;
  const hat = new THREE.Mesh(new THREE.ConeGeometry(0.17, 0.3, 18), hatMat);
  hat.position.y = 0.83;
  hat.rotation.z = 0.1;

  const orb = new THREE.Mesh(
    new THREE.SphereGeometry(0.07, 16, 12),
    new THREE.MeshStandardMaterial({
      color: shaded(color, 0.25),
      emissive: new THREE.Color(color),
      emissiveIntensity: 1.4,
      roughness: 0.2,
    }),
  );
  orb.position.set(0.26, 0.5, 0.14);

  g.add(robe, head, brim, hat, orb);
  return g;
}

/* --- 重戦士: 太い胴 + 肩アーマー + 盾 + 兜クレスト --- */
function buildHeavyweight(color) {
  const g = new THREE.Group();
  const armor = mat(shaded(color, -0.14), { roughness: 0.5, metalness: 0.25 });

  const body = new THREE.Mesh(
    new THREE.CylinderGeometry(0.26, 0.34, 0.55, 20),
    mat(color, { roughness: 0.5 }),
  );
  body.position.y = 0.275;

  const shoulderGeo = new THREE.BoxGeometry(0.18, 0.13, 0.2);
  const shoulderL = new THREE.Mesh(shoulderGeo, armor);
  shoulderL.position.set(-0.28, 0.56, 0);
  const shoulderR = new THREE.Mesh(shoulderGeo, armor);
  shoulderR.position.set(0.28, 0.56, 0);

  const head = new THREE.Mesh(new THREE.SphereGeometry(0.16, 18, 14), armor);
  head.position.y = 0.7;
  const crest = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.09, 0.2), mat(color));
  crest.position.y = 0.84;

  const shield = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.36, 0.3), armor);
  shield.position.set(-0.38, 0.34, 0.06);
  shield.rotation.y = 0.15;

  g.add(body, shoulderL, shoulderR, head, crest, shield);
  return g;
}

/* --- 探検家: つば付き帽子 + バックパック + 胸のコンパス --- */
function buildExplorer(color) {
  const g = new THREE.Group();
  const khaki = mat(0x9a8a5e, { roughness: 0.7 });
  const leather = mat(0x7a5a3a, { roughness: 0.8 });

  const body = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.26, 0.5, 18), mat(color));
  body.position.y = 0.25;

  const head = new THREE.Mesh(
    new THREE.SphereGeometry(0.15, 18, 14),
    mat(shaded(color, 0.18)),
  );
  head.position.y = 0.6;

  const brim = new THREE.Mesh(new THREE.CylinderGeometry(0.26, 0.26, 0.025, 20), khaki);
  brim.position.y = 0.69;
  const crown = new THREE.Mesh(new THREE.CylinderGeometry(0.13, 0.14, 0.13, 16), khaki);
  crown.position.y = 0.76;

  const pack = new THREE.Mesh(new THREE.BoxGeometry(0.24, 0.3, 0.13), leather);
  pack.position.set(0, 0.4, -0.26);
  const bedroll = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 0.26, 12), khaki);
  bedroll.rotation.z = Math.PI / 2;
  bedroll.position.set(0, 0.58, -0.26);

  const compass = new THREE.Mesh(
    new THREE.CylinderGeometry(0.05, 0.05, 0.02, 14),
    mat(0xd8b84a, { metalness: 0.6, roughness: 0.3 }),
  );
  compass.rotation.x = Math.PI / 2;
  compass.position.set(0, 0.45, 0.215);

  g.add(body, head, brim, crown, pack, bedroll, compass);
  return g;
}

/* --- 韋駄天: 前傾のスリムな体 + 頭の羽根 + 足元のスピードライン --- */
function buildSprinter(color) {
  const g = new THREE.Group();
  const body = new THREE.Mesh(new THREE.ConeGeometry(0.2, 0.6, 18), mat(color));
  body.position.y = 0.32;
  body.rotation.x = 0.22; // 前傾姿勢

  const head = new THREE.Mesh(
    new THREE.SphereGeometry(0.14, 18, 14),
    mat(shaded(color, 0.18)),
  );
  head.position.set(0, 0.66, 0.06);

  // 頭の翼（左右）
  const wingGeo = new THREE.ConeGeometry(0.05, 0.22, 10);
  const wingMat = mat(shaded(color, 0.28), { roughness: 0.3 });
  const wingL = new THREE.Mesh(wingGeo, wingMat);
  wingL.position.set(-0.15, 0.7, 0.02);
  wingL.rotation.z = 1.4;
  const wingR = new THREE.Mesh(wingGeo, wingMat);
  wingR.position.set(0.15, 0.7, 0.02);
  wingR.rotation.z = -1.4;

  // 足元のスピードライン（後方に流れる薄い板）
  const streak = new THREE.Mesh(
    new THREE.BoxGeometry(0.28, 0.02, 0.22),
    mat(shaded(color, 0.3), { roughness: 0.3 }),
  );
  streak.position.set(0, 0.05, -0.2);

  g.add(body, head, wingL, wingR, streak);
  return g;
}

/* --- 幻術師: 半透明のローブ + 浮遊する分身リング --- */
function buildPhantom(color) {
  const g = new THREE.Group();
  const ghost = mat(color, { transparent: true, opacity: 0.55, roughness: 0.3 });

  const body = new THREE.Mesh(new THREE.ConeGeometry(0.26, 0.66, 20), ghost);
  body.position.y = 0.33;

  const head = new THREE.Mesh(new THREE.SphereGeometry(0.15, 18, 14), ghost);
  head.position.y = 0.68;

  // 分身を示す浮遊リング
  const ring = new THREE.Mesh(
    new THREE.TorusGeometry(0.26, 0.02, 10, 28),
    mat(shaded(color, 0.3), {
      emissive: new THREE.Color(color),
      emissiveIntensity: 0.6,
      roughness: 0.25,
    }),
  );
  ring.rotation.x = Math.PI / 2;
  ring.position.y = 0.2;

  g.add(body, head, ring);
  return g;
}

/* --- 怪力: ずんぐりした太い胴 + 巨大な両腕（拳） --- */
function buildBrute(color) {
  const g = new THREE.Group();
  const skin = mat(shaded(color, -0.08), { roughness: 0.55 });

  const body = new THREE.Mesh(new THREE.CylinderGeometry(0.32, 0.36, 0.48, 20), mat(color));
  body.position.y = 0.24;

  const head = new THREE.Mesh(new THREE.SphereGeometry(0.16, 18, 14), skin);
  head.position.y = 0.6;

  const fistGeo = new THREE.SphereGeometry(0.16, 16, 12);
  const fistL = new THREE.Mesh(fistGeo, skin);
  fistL.position.set(-0.36, 0.18, 0.06);
  const fistR = new THREE.Mesh(fistGeo, skin);
  fistR.position.set(0.36, 0.18, 0.06);

  g.add(body, head, fistL, fistR);
  return g;
}

/* --- 呪術師: 暗色のフード + 発光する呪符（前面の板） --- */
function buildHexer(color) {
  const g = new THREE.Group();
  const dark = mat(shaded(color, -0.28), { roughness: 0.6 });

  const robe = new THREE.Mesh(new THREE.ConeGeometry(0.3, 0.62, 20), dark);
  robe.position.y = 0.31;

  const hood = new THREE.Mesh(new THREE.SphereGeometry(0.17, 18, 14), dark);
  hood.position.y = 0.66;

  // 顔の奥で光る目
  const glow = new THREE.Mesh(
    new THREE.SphereGeometry(0.05, 12, 10),
    new THREE.MeshStandardMaterial({
      color: shaded(color, 0.3),
      emissive: new THREE.Color(color),
      emissiveIntensity: 1.6,
      roughness: 0.2,
    }),
  );
  glow.position.set(0, 0.66, 0.13);

  // 浮遊する呪符
  const talisman = new THREE.Mesh(
    new THREE.BoxGeometry(0.12, 0.18, 0.015),
    mat(0xe8e0c8, { roughness: 0.5 }),
  );
  talisman.position.set(0.24, 0.42, 0.16);
  talisman.rotation.z = 0.3;

  g.add(robe, hood, glow, talisman);
  return g;
}

/* --- 盗賊: フード付きの細身 + 肩掛けの袋 + マスク --- */
function buildThief(color) {
  const g = new THREE.Group();
  const cloth = mat(shaded(color, -0.1), { roughness: 0.6 });

  const body = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.26, 0.52, 18), mat(color));
  body.position.y = 0.26;

  const head = new THREE.Mesh(
    new THREE.SphereGeometry(0.14, 18, 14),
    mat(shaded(color, 0.16)),
  );
  head.position.y = 0.62;

  // 目元のマスク帯
  const mask = new THREE.Mesh(new THREE.TorusGeometry(0.14, 0.03, 8, 20), cloth);
  mask.rotation.x = Math.PI / 2;
  mask.position.y = 0.64;

  // 肩掛けの盗品袋
  const sack = new THREE.Mesh(new THREE.SphereGeometry(0.14, 14, 12), cloth);
  sack.scale.set(1, 1.2, 1);
  sack.position.set(0.24, 0.3, -0.16);

  // 肩から袋へのストラップ
  const strap = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.4, 0.02), cloth);
  strap.position.set(0.06, 0.42, 0);
  strap.rotation.z = -0.5;

  g.add(body, head, mask, sack, strap);
  return g;
}

const BUILDERS = {
  none: buildClassic,
  ninja: buildNinja,
  mage: buildMage,
  heavyweight: buildHeavyweight,
  explorer: buildExplorer,
  sprinter: buildSprinter,
  phantom: buildPhantom,
  brute: buildBrute,
  hexer: buildHexer,
  thief: buildThief,
};

export const PIECE_CHARACTER_IDS = Object.keys(BUILDERS);

/** Build the pawn group for a character id (unknown ids → classic pawn). */
export function createCharacterPawn(characterId, playerIndex) {
  const color = PLAYER_COLORS[playerIndex === 1 ? 1 : 0];
  const build = BUILDERS[characterId] ?? BUILDERS.none;
  const g = build(color);
  g.traverse((o) => {
    if (o.isMesh) o.castShadow = true;
  });
  return g;
}

/** Dispose all geometries/materials of a pawn group built above. */
export function disposePawn(group) {
  group.traverse((o) => {
    if (!o.isMesh) return;
    o.geometry.dispose();
    if (Array.isArray(o.material)) o.material.forEach((m) => m.dispose());
    else o.material.dispose();
  });
}

/**
 * Render small transparent thumbnails of every character pawn for the setup
 * screen, using a one-shot offscreen WebGL renderer (disposed afterwards).
 * Returns { [characterId]: [dataURL_player0, dataURL_player1] }, or null if
 * a WebGL context is unavailable.
 */
export function renderPawnThumbnails(size = 96) {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: true,
      preserveDrawingBuffer: true,
    });
    renderer.setClearColor(0x000000, 0);
    renderer.setSize(size, size, false);

    const scene = new THREE.Scene();
    scene.add(new THREE.AmbientLight(0xffffff, 0.9));
    const dir = new THREE.DirectionalLight(0xffffff, 1.6);
    dir.position.set(2, 3, 2);
    scene.add(dir);

    const camera = new THREE.PerspectiveCamera(30, 1, 0.1, 10);
    camera.position.set(1.1, 1.0, 1.9);
    camera.lookAt(0, 0.42, 0);

    const out = {};
    for (const id of PIECE_CHARACTER_IDS) {
      out[id] = [];
      for (let p = 0; p < 2; p++) {
        const pawn = createCharacterPawn(id, p);
        pawn.rotation.y = -0.45; // 斜め前からのスリークォータービュー
        scene.add(pawn);
        renderer.render(scene, camera);
        out[id].push(canvas.toDataURL());
        scene.remove(pawn);
        disposePawn(pawn);
      }
    }

    renderer.dispose();
    if (typeof renderer.forceContextLoss === 'function') renderer.forceContextLoss();
    return out;
  } catch {
    return null;
  }
}
